import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

const MODULES = ['clients','leads','projects','invoices','servers','expenses','payroll','contracts','payment_gateways','subscriptions','users'];
const MODULE_LABELS = {clients:'Clients',leads:'Leads',projects:'Projects',invoices:'Invoices',servers:'Servers',expenses:'Expenses',payroll:'Payroll',contracts:'Contracts',payment_gateways:'Payment Gateways',subscriptions:'Subscriptions',users:'Users'};
const ACTIONS = ['view','add','edit','delete'];

const emptyPerms = () => MODULES.reduce((acc,m)=>({...acc,[m]:{view:false,add:false,edit:false,delete:false}}),{});
const fullPerms  = () => MODULES.reduce((acc,m)=>({...acc,[m]:{view:true,add:true,edit:true,delete:true}}),{});

export default function RolesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [name, setName]         = useState('');
  const [desc, setDesc]         = useState('');
  const [perms, setPerms]       = useState(emptyPerms());

  const { data, isLoading } = useQuery({
    queryKey:['roles'],
    queryFn:()=>api.get('/roles').then(r=>r.data),
  });

  const createMut = useMutation({
    mutationFn:()=>api.post('/roles',{name,description:desc,permissions:perms}),
    onSuccess:()=>{ qc.invalidateQueries(['roles']); resetForm(); toast.success('Role created'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updateMut = useMutation({
    mutationFn:()=>api.put('/roles/'+editing.id,{name,description:desc,permissions:perms}),
    onSuccess:()=>{ qc.invalidateQueries(['roles']); resetForm(); toast.success('Updated'); },
  });
  const deleteMut = useMutation({
    mutationFn:id=>api.delete('/roles/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['roles']); toast.success('Deleted'); },
  });

  const resetForm = () => { setShowForm(false); setEditing(null); setName(''); setDesc(''); setPerms(emptyPerms()); };

  const openEdit = role => {
    setEditing(role); setName(role.name); setDesc(role.description||'');
    const p = emptyPerms();
    if (role.permissions) Object.keys(role.permissions).forEach(m=>{ if(p[m]) p[m]={...p[m],...role.permissions[m]}; });
    setPerms(p); setShowForm(false);
  };

  const togglePerm   = (mod,action) => setPerms(p=>({...p,[mod]:{...p[mod],[action]:!p[mod][action]}}));
  const toggleAll    = mod => { const allOn=ACTIONS.every(a=>perms[mod][a]); setPerms(p=>({...p,[mod]:{view:!allOn,add:!allOn,edit:!allOn,delete:!allOn}})); };
  const setAllPerms  = val => setPerms(val?fullPerms():emptyPerms());

  const roles = data?.data || [];

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Roles</div><div className="stat-val">{roles.length}</div></div>
        <div className="card-sm"><div className="stat-label">Active</div><div className="stat-val" style={{color:'var(--green)'}}>{roles.filter(r=>r.is_active).length}</div></div>
        <div className="card-sm"><div className="stat-label">Modules</div><div className="stat-val">{MODULES.length}</div></div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setName('');setDesc('');setPerms(emptyPerms());}}>+ Create Role</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Role: '+editing.name:'New Role'}</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group"><label className="form-label">Role Name *</label>
              <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Sales Manager"/></div>
            <div className="form-group"><label className="form-label">Description</label>
              <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Role description..."/></div>
          </div>

          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div className="form-section-title" style={{margin:0}}>Module Permissions</div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setAllPerms(true)}>Select All</button>
              <button className="btn btn-outline btn-sm" onClick={()=>setAllPerms(false)}>Clear All</button>
            </div>
          </div>

          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--surface2)'}}>
                  <th style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase'}}>Module</th>
                  {ACTIONS.map(a=><th key={a} style={{padding:'10px 14px',textAlign:'center',fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase'}}>{a}</th>)}
                  <th style={{padding:'10px 14px',textAlign:'center',fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase'}}>All</th>
                </tr>
              </thead>
              <tbody>
                {MODULES.map((mod,idx)=>(
                  <tr key={mod} style={{borderTop:'1px solid var(--border)',background:idx%2===0?'#fff':'var(--surface2)'}}>
                    <td style={{padding:'12px 14px',fontWeight:600,fontSize:13}}>{MODULE_LABELS[mod]}</td>
                    {ACTIONS.map(action=>(
                      <td key={action} style={{padding:'12px 14px',textAlign:'center'}}>
                        <input type="checkbox" checked={perms[mod]?.[action]||false} onChange={()=>togglePerm(mod,action)}
                          style={{width:16,height:16,accentColor:'var(--green)',cursor:'pointer'}}/>
                      </td>
                    ))}
                    <td style={{padding:'12px 14px',textAlign:'center'}}>
                      <input type="checkbox" checked={ACTIONS.every(a=>perms[mod]?.[a])} onChange={()=>toggleAll(mod)}
                        style={{width:16,height:16,accentColor:'var(--green)',cursor:'pointer'}}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={()=>editing?updateMut.mutate():createMut.mutate()}>
              {editing?'Update Role':'Create Role'}
            </button>
            <button className="btn btn-outline" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead><tr><th>Role Name</th><th>Description</th><th>Module Access</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {roles.length===0&&<tr><td colSpan={5} className="empty-state">No roles found</td></tr>}
              {roles.map(r=>{
                const p = r.permissions||{};
                return (
                  <tr key={r.id}>
                    <td style={{fontWeight:700}}>{r.name}</td>
                    <td style={{color:'var(--muted)',fontSize:12}}>{r.description||'--'}</td>
                    <td>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        {Object.entries(p).filter(([,v])=>v.view).map(([mod])=>(
                          <span key={mod} className="badge badge-muted" style={{fontSize:10}}>{MODULE_LABELS[mod]||mod}</span>
                        ))}
                        {Object.values(p).filter(v=>v.view).length===0&&<span style={{color:'var(--muted)',fontSize:12}}>No access</span>}
                      </div>
                    </td>
                    <td><span className={"badge "+(r.is_active?'badge-green':'badge-danger')}>{r.is_active?'Active':'Inactive'}</span></td>
                    <td><div style={{display:'flex',gap:6}}>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEdit(r)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete role?'))deleteMut.mutate(r.id);}}>X</button>
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
